import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import classNames from "../../../utils/classNames";
import UserCardAvatar from "../UserCardAvatar";
import CardActionsMenu from "../CardActionsMenu";

import "./index.scss";

const HeartIcon = ({ filled }) => (
    <svg
        viewBox="0 0 24 24"
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M12 21s-7-4.5-9.5-9.5C.7 7.7 3.4 4 7 4c2 0 3.7 1.1 5 3 1.3-1.9 3-3 5-3 3.6 0 6.3 3.7 4.5 7.5C19 16.5 12 21 12 21z" />
    </svg>
);

HeartIcon.propTypes = { filled: PropTypes.bool };
HeartIcon.defaultProps = { filled: false };

const ReplyIcon = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <polyline points="9 17 4 12 9 7" />
        <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </svg>
);

const CommentBubble = ({
    commentId,
    userId,
    authorName,
    authorHandle,
    avatarUrl,
    avatarAlt,
    content,
    timestamp,
    timestampLabel,
    timestampDateTime,
    isAuthor,
    isReply,
    canManage,
    onReply,
    onEdit,
    onDelete,
    readOnly,
    className,
}) => {
    const { t } = useTranslation();
    const safeName = authorName || t("home.postCard.comments.anonymousAuthor");
    const altText = avatarAlt || safeName;

    const [liked, setLiked] = useState(false);
    const [likeBump, setLikeBump] = useState(0);
    const [isMenuOpen, setMenuOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [draftContent, setDraftContent] = useState(content);
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        if (!isEditing) {
            setDraftContent(content);
        }
    }, [content, isEditing]);

    // Close this bubble's menu when another comment opens its menu or the
    // user clicks outside the kebab/dropdown. Uses a custom DOM event for
    // cross-instance coordination — no parent prop-drilling required.
    useEffect(() => {
        if (!isMenuOpen) return undefined;
        const handleAnotherOpened = (event) => {
            if (event.detail !== commentId) {
                setMenuOpen(false);
            }
        };
        const handleDocumentMouseDown = (event) => {
            const inActions = event.target.closest?.(".post-card__actions");
            const inDropdown = event.target.closest?.(".post-card__dropdown");
            if (!inActions && !inDropdown) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("comment-menu-opened", handleAnotherOpened);
        document.addEventListener("mousedown", handleDocumentMouseDown);
        return () => {
            document.removeEventListener("comment-menu-opened", handleAnotherOpened);
            document.removeEventListener("mousedown", handleDocumentMouseDown);
        };
    }, [isMenuOpen, commentId]);

    const handleToggleLike = useCallback(() => {
        setLiked((prev) => !prev);
        setLikeBump((value) => value + 1);
    }, []);

    const handleToggleMenu = useCallback(() => {
        setMenuOpen((prev) => {
            const next = !prev;
            if (next && typeof document !== "undefined") {
                // Notify other CommentBubble instances to close their menus.
                document.dispatchEvent(
                    new CustomEvent("comment-menu-opened", { detail: commentId })
                );
            }
            return next;
        });
    }, [commentId]);

    const handleStartEdit = useCallback(() => {
        setDraftContent(content);
        setIsEditing(true);
        setMenuOpen(false);
    }, [content]);

    const handleCancelEdit = useCallback(() => {
        setDraftContent(content);
        setIsEditing(false);
    }, [content]);

    const handleSaveEdit = useCallback(async () => {
        const nextContent = draftContent.trim();
        if (!nextContent || nextContent === content || isSavingEdit) {
            setIsEditing(false);
            setDraftContent(content);
            return;
        }

        setIsSavingEdit(true);
        try {
            await onEdit?.(nextContent);
            setIsEditing(false);
        } catch (error) {
            console.error("Failed to update comment", error);
        } finally {
            setIsSavingEdit(false);
        }
    }, [content, draftContent, isSavingEdit, onEdit]);

    const handleEditKeyDown = useCallback((event) => {
        if (event.key === "Escape") {
            event.preventDefault();
            handleCancelEdit();
            return;
        }
        if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
            event.preventDefault();
            handleSaveEdit();
        }
    }, [handleCancelEdit, handleSaveEdit]);

    const handleDelete = useCallback(async () => {
        if (isDeleting) {
            return;
        }
        setMenuOpen(false);
        setIsDeleting(true);
        try {
            await onDelete?.();
        } catch (error) {
            console.error("Failed to delete comment", error);
        } finally {
            setIsDeleting(false);
        }
    }, [isDeleting, onDelete]);

    const menuActions = useMemo(() => [
        {
            id: "edit",
            label: t("home.postCard.actions.edit"),
            onSelect: handleStartEdit,
            disabled: isDeleting,
        },
        {
            id: "delete",
            label: t("home.postCard.actions.delete"),
            onSelect: handleDelete,
            disabled: isSavingEdit,
        },
    ], [handleDelete, handleStartEdit, isDeleting, isSavingEdit, t]);

    return (
        <div
            className={classNames("comment-bubble", className, {
                "comment-bubble--author": isAuthor,
                "comment-bubble--reply": isReply,
                "comment-bubble--editing": isEditing,
            })}
            role="listitem"
        >
            <div className="comment-bubble__avatar">
                <UserCardAvatar
                    userId={userId || undefined}
                    username={safeName}
                    avatarUrl={avatarUrl || undefined}
                    alt={altText}
                    className="comment-bubble__avatar-media"
                    avatarClassName="comment-bubble__avatar-image"
                    enableProfilePreview={Boolean(userId)}
                />
            </div>

            <div className="comment-bubble__body">
                <div className="comment-bubble__bubble">
                    <div className="comment-bubble__head">
                        <span className="comment-bubble__name">{safeName}</span>
                        {authorHandle && (
                            <span className="comment-bubble__handle">{authorHandle}</span>
                        )}
                        {isAuthor && (
                            <span className="comment-bubble__author-tag">
                                {t("home.postCard.comments.authorTag")}
                            </span>
                        )}
                        {timestamp && (
                            <time
                                className="comment-bubble__time"
                                dateTime={timestampDateTime || undefined}
                                title={timestampLabel || undefined}
                            >
                                {timestamp}
                            </time>
                        )}
                        {!readOnly && canManage && commentId && !isEditing && (
                            <div className="comment-bubble__menu">
                                <CardActionsMenu
                                    isOpen={isMenuOpen}
                                    onToggle={handleToggleMenu}
                                    itemId={String(commentId)}
                                    actions={menuActions}
                                    disabled={isDeleting}
                                />
                            </div>
                        )}
                    </div>
                    {isEditing ? (
                        <div className="comment-bubble__edit">
                            <textarea
                                className="comment-bubble__edit-input"
                                value={draftContent}
                                onChange={(event) => setDraftContent(event.target.value)}
                                onKeyDown={handleEditKeyDown}
                                disabled={isSavingEdit}
                                rows={Math.max(2, Math.min(6, draftContent.split("\n").length + 1))}
                                autoFocus
                            />
                            <div className="comment-bubble__edit-actions">
                                <button
                                    type="button"
                                    className="comment-bubble__edit-action"
                                    onClick={handleCancelEdit}
                                    disabled={isSavingEdit}
                                >
                                    {t("home.postCard.editor.cancelLabel")}
                                </button>
                                <button
                                    type="button"
                                    className="comment-bubble__edit-action comment-bubble__edit-action--save"
                                    onClick={handleSaveEdit}
                                    disabled={isSavingEdit || draftContent.trim().length === 0}
                                >
                                    {t("home.postCard.editor.saveLabel")}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <p className="comment-bubble__text">{content}</p>
                    )}
                </div>

                {!isEditing && !readOnly && (
                    <div className="comment-bubble__actions">
                        <button
                            type="button"
                            className={classNames("comment-action", {
                                "comment-action--liked": liked,
                            })}
                            onClick={handleToggleLike}
                            aria-pressed={liked}
                        >
                            <span
                                className="comment-action__icon"
                                key={likeBump}
                                data-bump={likeBump > 0 ? "true" : undefined}
                            >
                                <HeartIcon filled={liked} />
                            </span>
                            <span>
                                {liked
                                    ? t("home.postComments.actions.liked")
                                    : t("home.postComments.actions.like")}
                            </span>
                        </button>
                        {!isReply && (
                            <button
                                type="button"
                                className="comment-action"
                                onClick={onReply || undefined}
                            >
                                <span className="comment-action__icon">
                                    <ReplyIcon />
                                </span>
                                <span>{t("home.postComments.actions.reply")}</span>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

CommentBubble.propTypes = {
    commentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userId: PropTypes.string,
    authorName: PropTypes.string.isRequired,
    authorHandle: PropTypes.string,
    avatarUrl: PropTypes.string,
    avatarAlt: PropTypes.string,
    content: PropTypes.string.isRequired,
    timestamp: PropTypes.string,
    timestampLabel: PropTypes.string,
    timestampDateTime: PropTypes.string,
    isAuthor: PropTypes.bool,
    isReply: PropTypes.bool,
    canManage: PropTypes.bool,
    onReply: PropTypes.func,
    onEdit: PropTypes.func,
    onDelete: PropTypes.func,
    readOnly: PropTypes.bool,
    className: PropTypes.string,
};

CommentBubble.defaultProps = {
    commentId: null,
    userId: null,
    authorHandle: "",
    avatarUrl: null,
    avatarAlt: "",
    timestamp: "",
    timestampLabel: "",
    timestampDateTime: "",
    isAuthor: false,
    isReply: false,
    canManage: false,
    onReply: null,
    onEdit: null,
    onDelete: null,
    readOnly: false,
    className: "",
};

export default CommentBubble;
